import React, { useEffect, useState } from 'react';
import { Button, Modal, Typography, Result } from 'antd';
import { ErrorRender } from '@partsbadger/library';
import { observer } from 'mobx-react';
import { useHistory, useLocation } from 'react-router-dom';

import UserStore from 'stores/UserStore';
import UIStore from '@/stores/UIStore';

import SignIn from './SignIn';
import SignUp from './SignUp';
import ResetPassword from './ResetPassword';
interface Props {
    // first_name?: string;
    // last_name?: string;
    // email?: string;
    // company?: string;
    handleLogin?: () => void;
    onLoginSuccess?: () => void;
    onRegisterSuccess?: () => void;
}

type FormType = 'login' | 'register' | 'reset-password';

const FORM_CONFIG: Record<FormType, { title: string; maxWidth: string }> = {
    login: { title: 'Login', maxWidth: '440px' },
    register: { title: 'Register', maxWidth: '640px' },
    'reset-password': { title: 'Reset Password', maxWidth: '440px' },
};

const AuthLayout: React.FC<Props> = observer(props => {
    const history = useHistory();
    const { pathname, search, state } = useLocation();
    const data: any = state;

    const [quoteSuccessModalOpen, setQuoteSuccessModalOpen] = useState(!!data?.quoteSubmitted);

    const closeQuoteSuccessModal = () => {
        setQuoteSuccessModalOpen(false);
        if (data?.quoteSubmitted && typeof data === 'object') {
            history.replace({
                pathname,
                search,
                state: { ...data, quoteSubmitted: false },
            });
        }
    };

    // Preserve ALL query params when navigating between auth routes
    const goTo = (nextPathname: string) => {
        UserStore.setError(null);
        history.push({
            pathname: nextPathname,
            search,
        });
    };

    // Normalize path → form type (default login when root "/" or unknown)
    const raw = pathname.replace(/^\/|\/$/g, ''); // "login", "register", "reset-password", or ""
    const formType: FormType = raw === 'register' || raw === 'reset-password' ? raw : 'login';

    const errorMessage: React.ReactNode | null = UserStore.errors ? <ErrorRender error={UserStore.errors} /> : null;

    useEffect(() => {
        UIStore.setSelectedKey(['my-account']);
        UIStore.setDocumentTitle(FORM_CONFIG[formType].title);
    }, [formType]);

    const { first_name, last_name, email, company } = UserStore.guest_data || {};

    const propsGuestData = {
        first_name: first_name,
        last_name: last_name,
        email: email,
        company: company,
    };

    const renderForm = () => {
        switch (formType) {
            case 'register':
                return (
                    <>
                        <SignUp {...propsGuestData} />
                        <div className="mt-2">
                            Already have an account?
                            <Button type="link" className="p-2 font-weight-bold" onClick={() => goTo('/login/')}>
                                Login
                            </Button>
                        </div>
                    </>
                );

            case 'reset-password':
                return <ResetPassword />;

            case 'login':
            default:
                return (
                    <>
                        <SignIn {...propsGuestData} />
                        <div>
                            <Button type="default" block className="mt-2" onClick={() => goTo('/register/')}>
                                Create an Account
                            </Button>
                        </div>
                    </>
                );
        }
    };

    return (
        <div
            style={{
                width: '100%',
                maxWidth: FORM_CONFIG[formType].maxWidth,
            }}
        >
            <Typography.Title level={1}>{FORM_CONFIG[formType].title}</Typography.Title>

            <Modal
                title={null}
                open={quoteSuccessModalOpen}
                onOk={closeQuoteSuccessModal}
                onCancel={closeQuoteSuccessModal}
                footer={[
                    <Button key="ok" type="primary" onClick={closeQuoteSuccessModal}>
                        Continue
                    </Button>,
                ]}
                width={600}
                maskClosable
                destroyOnClose
            >
                <Result
                    status="success"
                    title="Your Quote was sent!"
                    subTitle="Thank you for using PartsBadger's Quote submission form. We will email you a quote shortly!"
                />
            </Modal>

            {data?.isNew ? (
                <div className="text-center mt-2">
                    <div className="font-bold">Setup Account (Optional)</div>
                    <div style={{ color: 'rgba(0, 0, 0, 0.45)' }}>
                        Complete account setup to manage quotes, orders and account requirements
                    </div>
                </div>
            ) : null}

            {errorMessage}

            {renderForm()}

            {formType === 'login' ? (
                <div className="d-flex">
                    <Button type="link" className="ml-auto p-0 m-0" onClick={() => goTo('/reset-password/')}>
                        Forgot your password?
                    </Button>
                </div>
            ) : null}
        </div>
    );
});

export default AuthLayout;
